import { useEffect, useState } from 'react';
import { foldersApi } from '@/shared/folders';

/**
 * `user_id → display name` for the active organization.
 *
 * `folders_nodes.created_by` is a uuid and every screen that wants to name an
 * owner has to resolve it the same way, through `public.org_directory`. People
 * outside the organization are absent from that RPC; the only outsiders named
 * are the creators of documents shared directly with this person, by display
 * name only, never email.
 *
 * Failures resolve to an empty map on purpose: a name that will not load must
 * not take a whole section down with it.
 */
export function useOrgDirectory(orgId: string | null): ReadonlyMap<string, string | null> {
  const [names, setNames] = useState<ReadonlyMap<string, string | null>>(new Map());

  useEffect(() => {
    if (!orgId) {
      setNames(new Map());
      return;
    }
    const controller = new AbortController();

    void (async () => {
      try {
        // Owners of documents shared from other organizations come first so an
        // org member's entry wins if someone is in both.
        const [members, sharedOwners] = await Promise.all([
          foldersApi.listOrgMembers(orgId, controller.signal),
          foldersApi.listSharedOwnerNames(controller.signal).catch(() => []),
        ]);
        if (controller.signal.aborted) return;
        setNames(
          new Map([
            ...sharedOwners.map((owner) => [owner.userId, owner.fullName] as const),
            ...members.map((member) => [member.userId, member.fullName] as const),
          ]),
        );
      } catch {
        if (!controller.signal.aborted) setNames(new Map());
      }
    })();

    return () => controller.abort();
  }, [orgId]);

  return names;
}
